import { useEffect, useState } from 'react';

import { DisplayComponent } from '@/components/DisplayComponent';
import { DisplayHeading } from '@/components/DisplayHeading';
import { Header } from '@/components/Header';
import { MultipleUserInput } from '@/components/MultipleUserInput';
import { ProgressBar } from '@/components/ProgressBar';
import { Slogan } from '@/components/Slogan';
import {
    EVehicleAssetType,
    selectIsAssetTypeKnownViaParams,
    selectIsVehicleKnown,
    selectVehicleAssetType,
} from '@/context/borrower';
import {
    EDisplays,
    selectHideVehicleScreen,
    selectIsAssetKnownViaParams,
    selectPartner,
    selectPreviousDisplay,
} from '@/context/display';
import { store } from '@/context/store';
import { useAppSelector } from '@/context/storeHooks';
import { useScreenCopy } from '@/copy';
import { Balloon } from '@/UserInput/Balloon';
import { Deposit } from '@/UserInput/Deposit';
import { LoanUsage } from '@/UserInput/LoanUsage';
import { TradeIn } from '@/UserInput/TradeIn';
import { VehicleAssetType } from '@/UserInput/VehicleAssetType';
import { VehicleCondition } from '@/UserInput/VehicleCondition';
import { VehicleKnown } from '@/UserInput/VehicleKnown';

import { LoanButtons } from './LoanButtons';

export const testId = 'Loan';

export function Loan(): JSX.Element {
    // ***** CONSTANTS *****
    const baseId = 'screen-loanDetails';
    const screenCopy = useScreenCopy('loan');
    const previousDisplay = useAppSelector(selectPreviousDisplay);
    const hideVehicleScreen = useAppSelector(selectHideVehicleScreen);
    const isAssetKnownViaParams = useAppSelector(selectIsAssetKnownViaParams);
    const isAssetTypeKnownViaParams = useAppSelector(
        selectIsAssetTypeKnownViaParams
    );
    const isAssetTypeEquipment =
        useAppSelector(selectVehicleAssetType) ===
        EVehicleAssetType.PLANT_EQUIPMENT;
    const wasPreviousDisplayIndicativelyApproved =
        previousDisplay === EDisplays.INDICATIVELY_APPROVED;
    const isVehicleKnown = useAppSelector(selectIsVehicleKnown);
    // Already asked on the quick calculator, so don't ask again here
    const askedNewOrUsedUpFront =
        useAppSelector(selectPartner)?.showNewOrUsedCarToggle;

    const [showDoYouKnowTheVehicleButtons, setShowDoYouKnowTheVehicleButtons] =
        useState(true);

    useEffect(() => {
        setShowDoYouKnowTheVehicleButtons(showDoYouKnowTheVehicle());
    }, [isAssetKnownViaParams, hideVehicleScreen]);

    function showDoYouKnowTheVehicle(): boolean {
        if (isAssetKnownViaParams) {
            if (!hideVehicleScreen) {
                return true;
            }
            return false;
        }
        return true;
    }
    const state = store.getState();
    const isNovatedLease = state.borrowerReducer.loan.loanUsage === 'NOVATED';
    const isBrandNovated = state.displayReducer.partner.isNovatedLease;

    return (
        <DisplayComponent baseId={baseId} data-testid={testId}>
            <Header />
            <Slogan />
            <ProgressBar />
            {screenCopy.title && (
                <DisplayHeading
                    title={screenCopy.title}
                    infoText={screenCopy.subtitle}
                />
            )}
            {!isBrandNovated && (
                <LoanUsage showErrors={false} baseId={baseId} />
            )}
            {!isAssetTypeKnownViaParams && (
                <VehicleAssetType showErrors={false} baseId={baseId} />
            )}
            {!isNovatedLease && (
                <>
                    <MultipleUserInput>
                        <TradeIn showErrors={false} baseId={baseId} />
                        <Deposit showErrors={false} baseId={baseId} />
                    </MultipleUserInput>
                    <MultipleUserInput>
                        <Balloon showErrors={false} baseId={baseId} />
                        <div className='w-full' />
                    </MultipleUserInput>
                </>
            )}

            {!isAssetTypeEquipment &&
                !wasPreviousDisplayIndicativelyApproved &&
                showDoYouKnowTheVehicleButtons && (
                    <VehicleKnown showErrors={false} baseId={baseId} />
                )}
            {!isAssetTypeEquipment &&
                showDoYouKnowTheVehicleButtons &&
                isVehicleKnown &&
                !askedNewOrUsedUpFront && (
                    <VehicleCondition showErrors={false} baseId={baseId} />
                )}
            <LoanButtons baseId={baseId} />
        </DisplayComponent>
    );
}
